package com.mgacreative.hotel.domain.repository

import com.mgacreative.hotel.core.network.SupabaseNetworkClient
import com.mgacreative.hotel.core.tenant.TenantSession
import com.mgacreative.hotel.domain.model.Shift
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

interface ShiftRepository {
    val shifts: StateFlow<List<Shift>>
    suspend fun loadShiftsForDate(date: String)
    suspend fun addShift(shift: Shift): Boolean
    suspend fun updateShiftStatus(shiftId: String, status: String): Boolean
}

class ShiftRepositoryImpl(
    private val networkClient: SupabaseNetworkClient,
    private val tenantSession: TenantSession
) : ShiftRepository {
    private val _shifts = MutableStateFlow<List<Shift>>(emptyList())
    override val shifts: StateFlow<List<Shift>> = _shifts.asStateFlow()

    override suspend fun loadShiftsForDate(date: String) {
        if (tenantSession.isDemoMode) {
            _shifts.value = com.mgacreative.hotel.domain.demo.DemoDataProvider.shiftsState.value
            return
        }
        try {
            val tenantId = tenantSession.currentTenantId
            val response = if (!tenantId.isNullOrBlank()) {
                networkClient.client.postgrest["staff_shifts"]
                    .select {
                        filter {
                            eq("tenant_id", tenantId)
                            eq("date", date)
                        }
                    }.decodeList<Shift>()
            } else {
                networkClient.client.postgrest["staff_shifts"]
                    .select {
                        filter {
                            eq("date", date)
                        }
                    }.decodeList<Shift>()
            }
            if (response.isNotEmpty()) {
                _shifts.value = response
            }
        } catch (e: Exception) {
            e.printStackTrace()
        }
    }

    override suspend fun addShift(shift: Shift): Boolean {
        val tenantId = tenantSession.currentTenantId ?: "default_tenant"
        val isValidTenantUuid = tenantId.contains("-") && tenantId.length >= 32
        val newShift = shift.copy(
            id = if (shift.id.isBlank()) "shift_${kotlin.random.Random.nextInt(10000, 99999)}" else shift.id,
            tenant_id = if (isValidTenantUuid) tenantId else ""
        )
        // Add to local state immediately
        _shifts.value = _shifts.value + newShift

        if (isValidTenantUuid) {
            try {
                networkClient.client.postgrest["staff_shifts"].insert(newShift)
                loadShiftsForDate(shift.date)
            } catch (e: Exception) {
                e.printStackTrace()
            }
        }
        return true
    }

    override suspend fun updateShiftStatus(shiftId: String, status: String): Boolean {
        return try {
            val tenantId = tenantSession.currentTenantId
            if (tenantId != null) {
                networkClient.client.postgrest["staff_shifts"]
                    .update({ set("status", status) }) {
                        filter {
                            eq("id", shiftId)
                            eq("tenant_id", tenantId)
                        }
                    }
                true
            } else {
                false
            }
        } catch (e: Exception) {
            e.printStackTrace()
            false
        }
    }
}
